/**
 * Typed access to Vite-injected environment values for the public site and member app.
 *
 * Defaults target the local docker-compose stack so the app boots without a `.env`;
 * deployed values are supplied per environment at build time.
 */

interface WebEnvironmentValues {
  /** Base URL of the API. Auth endpoints live under `/api/auth/*` there. */
  readonly apiUrl: string;
  /** Where organizers sign in. Linked from the marketing site's "For organizers" call to action. */
  readonly organizerUrl: string;
}

function readEnvironmentValue(name: string): string | undefined {
  // `import.meta.env` is defined by Vite at build time and is **undefined** under any other
  // loader — which now includes the DOM test runner (ADR 043), where a component tree that
  // renders `AuthProvider` reaches this on its first render. Indexing `undefined` threw a
  // `TypeError` from inside a `useMemo`, surfacing as a React render crash three frames from
  // anything to do with configuration. Falling through to the defaults below is what this
  // function already does for a missing variable; this is the same answer for a missing *table*.
  const environment = import.meta.env as Record<string, string | undefined> | undefined;
  const value = environment?.[name];
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

export function getWebEnvironment(): WebEnvironmentValues {
  return {
    apiUrl: readEnvironmentValue('VITE_API_URL') ?? 'http://localhost:5200',
    organizerUrl: readEnvironmentValue('VITE_ORGANIZER_URL') ?? 'http://localhost:5203',
  };
}

/**
 * **The payment provider's publishable key, or `null` when this build has none** (`PAY-004`).
 *
 * Not part of {@link WebEnvironmentValues}, and deliberately so: every value there has a working
 * default, because a missing API URL is a machine that has not been configured. A missing
 * publishable key is not that. It is the platform saying *this build takes no card payments*,
 * which `resolveCardElements()` turns into the checkout's stated absence (ADR 045). A default
 * here would be a key that belongs to somebody, which is the one thing it must never be — so the
 * absence is returned as an absence, and `string | null` makes the caller decide what to do
 * about it.
 *
 * **A publishable key is public by design.** The provider embeds it in client-side JavaScript
 * served to every visitor, and it is inlined into this bundle at build time regardless. It can
 * mint a payment method against the provider's own account and can do nothing else — it cannot
 * charge, refund, or read anything. The **secret** key is a different value with a different
 * lifecycle: it lives only in Secrets Manager as `payments/provider-secret-key`, is granted to
 * the API alone, and must never appear in this repository or in any bundle (ADR 045).
 */
export function getPaymentPublishableKey(): string | null {
  return readEnvironmentValue('VITE_PAYMENT_PUBLISHABLE_KEY') ?? null;
}

/**
 * Whether this is a Vite development build.
 *
 * Read through the same defensive path as everything else above, and for the same reason: outside
 * Vite `import.meta.env` is `undefined`, and `import.meta.env.DEV` on it is a `TypeError` thrown
 * from inside whichever effect asked. Unknown answers **false** — a non-Vite loader is not a dev
 * server, and the caller's question is only ever "should I stand down for the dev server".
 */
export function isDevelopmentBuild(): boolean {
  const environment = import.meta.env as { readonly DEV?: unknown } | undefined;
  return environment?.DEV === true;
}
